import { createSelector } from '@reduxjs/toolkit';
import { TStoreState } from 'redux/types';

export const selectOrderManagement = (state: TStoreState) => state.orderManagement;

export const selectOrderList = (state: TStoreState) => selectOrderManagement(state).orderList;

export const selectOrderListLoading = (state: TStoreState) =>
	selectOrderManagement(state).orderListLoading;

const EMPTY_ORDER_ROWS: never[] = [];

export const selectOrderListData = createSelector(
	[selectOrderList],
	(orderList) => orderList?.rows ?? EMPTY_ORDER_ROWS,
);

export const selectOrderListTotal = (state: TStoreState) => {
	const orderList = selectOrderList(state);
	return orderList?.rowCount || 0;
};

export const selectOrderListPage = (state: TStoreState) => {
	const orderList = selectOrderList(state);
	return orderList?.page || null;
};

export const selectOrderListPageSize = (state: TStoreState) => {
	const orderList = selectOrderList(state);
	return orderList?.page?.size || 100;
};

export const selectOrderListPageCurrent = (state: TStoreState) => {
	const orderList = selectOrderList(state);
	return orderList?.page?.current || 1;
};

export const selectOrderListTotalPages = (state: TStoreState) => {
	const orderList = selectOrderList(state);
	return orderList?.page?.total || 0;
};

// Order Splits

export const selectOrderSplits = (state: TStoreState) => selectOrderManagement(state).orderSplits;

export const selectOrderSplitsLoading = (state: TStoreState) =>
	selectOrderManagement(state).orderSplitsLoading;

const EMPTY_SPLITS_ROWS: never[] = [];

export const selectOrderSplitsData = createSelector(
	[selectOrderSplits],
	(orderSplits) => orderSplits?.rows ?? EMPTY_SPLITS_ROWS,
);

export const selectOrderSplitsTotal = (state: TStoreState) => {
	const orderSplits = selectOrderSplits(state);
	return orderSplits?.rowCount || 0;
};

// Order Items

export const selectOrderItems = (state: TStoreState) => selectOrderManagement(state).orderItems;

export const selectOrderItemsLoading = (state: TStoreState) =>
	selectOrderManagement(state).orderItemsLoading;

const EMPTY_ITEMS_ROWS: never[] = [];

export const selectOrderItemsData = createSelector(
	[selectOrderItems],
	(orderItems) => orderItems?.rows ?? EMPTY_ITEMS_ROWS,
);

export const selectOrderItemsTotal = (state: TStoreState) => {
	const orderItems = selectOrderItems(state);
	return orderItems?.rowCount || 0;
};

// Order Returns

export const selectOrderReturns = (state: TStoreState) => selectOrderManagement(state).orderReturns;

export const selectOrderReturnsLoading = (state: TStoreState) =>
	selectOrderManagement(state).orderReturnsLoading;

const EMPTY_ORDER_RETURNS_ROWS: never[] = [];

export const selectOrderReturnsData = createSelector(
	[selectOrderReturns],
	(orderReturns) => orderReturns?.rows ?? EMPTY_ORDER_RETURNS_ROWS,
);

export const selectOrderReturnsTotal = (state: TStoreState) => {
	const orderReturns = selectOrderReturns(state);
	return orderReturns?.rowCount || 0;
};

export const selectOrderReturnsPage = (state: TStoreState) => {
	const orderReturns = selectOrderReturns(state);
	return orderReturns?.page || null;
};

// Order Discounts

export const selectOrderDiscounts = (state: TStoreState) =>
	selectOrderManagement(state).orderDiscounts;

export const selectOrderDiscountsLoading = (state: TStoreState) =>
	selectOrderManagement(state).orderDiscountsLoading;

const EMPTY_ORDER_DISCOUNTS_ROWS: never[] = [];

export const selectOrderDiscountsData = createSelector(
	[selectOrderDiscounts],
	(orderDiscounts) => orderDiscounts?.rows ?? EMPTY_ORDER_DISCOUNTS_ROWS,
);

export const selectOrderDiscountsTotal = (state: TStoreState) => {
	const orderDiscounts = selectOrderDiscounts(state);
	return orderDiscounts?.rowCount || 0;
};

export const selectOrderDiscountsPage = (state: TStoreState) => {
	const orderDiscounts = selectOrderDiscounts(state);
	return orderDiscounts?.page || null;
};

// Order Discount Breakdown

export const selectOrderDiscountBreakdown = (state: TStoreState) =>
	selectOrderManagement(state).orderDiscountBreakdown;

export const selectOrderDiscountBreakdownLoading = (state: TStoreState) =>
	selectOrderManagement(state).orderDiscountBreakdownLoading;

const EMPTY_ORDER_DISCOUNT_BREAKDOWN_ROWS: never[] = [];

export const selectOrderDiscountBreakdownData = createSelector(
	[selectOrderDiscountBreakdown],
	(orderDiscountBreakdown) => orderDiscountBreakdown?.rows ?? EMPTY_ORDER_DISCOUNT_BREAKDOWN_ROWS,
);

export const selectOrderDiscountBreakdownTotal = (state: TStoreState) => {
	const orderDiscountBreakdown = selectOrderDiscountBreakdown(state);
	return orderDiscountBreakdown?.rowCount || 0;
};

// Order Return Items

export const selectOrderReturnItems = (state: TStoreState) =>
	selectOrderManagement(state).orderReturnItems;

export const selectOrderReturnItemsLoading = (state: TStoreState) =>
	selectOrderManagement(state).orderReturnItemsLoading;

const EMPTY_ORDER_RETURN_ITEMS_ROWS: never[] = [];

export const selectOrderReturnItemsData = createSelector(
	[selectOrderReturnItems],
	(orderReturnItems) => orderReturnItems?.rows ?? EMPTY_ORDER_RETURN_ITEMS_ROWS,
);

export const selectOrderReturnItemsTotal = (state: TStoreState) => {
	const orderReturnItems = selectOrderReturnItems(state);
	return orderReturnItems?.rowCount || 0;
};

// Split items map (keyed by split_id)

export const selectSplitItemsMap = (state: TStoreState) =>
	selectOrderManagement(state).splitItemsMap;

export const selectSplitItemsLoadingMap = (state: TStoreState) =>
	selectOrderManagement(state).splitItemsLoadingMap;

// Swiss Declarations

export const selectSwissDeclarations = (state: TStoreState) =>
	selectOrderManagement(state).swissDeclarations;

export const selectSwissDeclarationsLoading = (state: TStoreState) =>
	selectOrderManagement(state).swissDeclarationsLoading;

export const selectSwissDeclarationDownloadLoading = (state: TStoreState) =>
	selectOrderManagement(state).swissDeclarationDownloadLoading;

const EMPTY_SWISS_DECLARATIONS_ROWS: never[] = [];

export const selectSwissDeclarationsData = createSelector(
	[selectSwissDeclarations],
	(swissDeclarations) => swissDeclarations?.data ?? EMPTY_SWISS_DECLARATIONS_ROWS,
);

export const selectSwissDeclarationsTotal = (state: TStoreState) => {
	const swissDeclarations = selectSwissDeclarations(state);
	return swissDeclarations?.totalCount || 0;
};
